/*
  HLL visual pass: the Instagram row moves on its own.

  The Instafeed app shows the posts as a slider with two arrows. This turns
  that row into a slow, endless ticker on every page that has it, on phone
  and desktop. It pauses while the pointer is over it, while a post has
  keyboard focus, for a few seconds after a touch or drag, while a post's
  popup is open, and while it is off screen. It can be dragged or swiped sideways. A tap or click on a
  post still opens the app's own popup.

  How: the app's row (.instafeed-new-layout-wrapper) is moved with a CSS
  transform, a little each frame. When the first post has fully left the
  screen it is moved to the end of the row, so the row never runs out. The
  posts are the app's own elements, nothing is copied. The app opens popups
  by post id, so the changing order does not affect them.

  While the ticker runs, the app's arrows are hidden and its own slide
  transition is switched off (section 22 of assets/hll-visual-pass.css).
  With "reduce motion" switched on, or if this script does not run, the row
  stays the app's normal slider with its arrows.
*/
(function () {
  'use strict';

  var SPEED_MOUSE = 30; // pixels per second with a mouse
  var SPEED_TOUCH = 40; // pixels per second on a touch screen
  var RESUME_AFTER = 3000; // ms the row waits after a touch, drag or focus
  var DRAG_START = 6; // px of sideways movement before it counts as a drag

  function start(container) {
    if (container.hllTicker) return true;
    var wrapper = container.querySelector('.instafeed-new-layout-wrapper');
    if (!wrapper || wrapper.children.length < 4) return false;
    var first = wrapper.firstElementChild.getBoundingClientRect();
    if (!first.width) return false; // the app has not sized the posts yet
    if (wrapper.scrollWidth <= container.clientWidth + first.width) return false; // too few posts to loop
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;

    container.hllTicker = true;
    container.classList.add('hll-ig-ticker');

    var touch = window.matchMedia('(hover: none) and (pointer: coarse)');
    var pos = 0; // how far the row has moved left, in px
    var hovered = false;
    var held = false;
    var holdTimer;
    var lastFrame = 0;
    var drag = null;
    var dragged = false;

    function gap() {
      return parseFloat(window.getComputedStyle(wrapper).columnGap) || 0;
    }

    // Keep pos between 0 and one post width by moving posts from one end
    // of the row to the other
    function apply() {
      var guard = 0;
      var item = wrapper.firstElementChild;
      var width = item ? item.getBoundingClientRect().width + gap() : 0;
      while (width && pos >= width && guard++ < 50) {
        wrapper.appendChild(item);
        pos -= width;
        item = wrapper.firstElementChild;
        width = item.getBoundingClientRect().width + gap();
      }
      while (pos < 0 && guard++ < 50) {
        item = wrapper.lastElementChild;
        width = item.getBoundingClientRect().width + gap();
        if (!width) break;
        wrapper.insertBefore(item, wrapper.firstChild);
        pos += width;
      }
      wrapper.style.setProperty('transform', 'translate3d(' + -pos.toFixed(2) + 'px, 0, 0)', 'important');
    }

    function hold() {
      held = true;
      window.clearTimeout(holdTimer);
    }

    function release() {
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(function () {
        held = false;
      }, RESUME_AFTER);
    }

    function onScreen() {
      var rect = container.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    // The app marks the page while one of its popups is open
    function popupOpen() {
      return document.body.classList.contains('instafeed-body-no-scroll');
    }

    function frame(now) {
      if (!wrapper.isConnected) {
        // The app rebuilt the row. Let the watcher set the ticker up again.
        container.hllTicker = false;
        return;
      }
      var elapsed = lastFrame ? Math.min(now - lastFrame, 100) : 0;
      lastFrame = now;
      if (elapsed && !hovered && !held && !drag && !popupOpen() && onScreen()) {
        pos += ((touch.matches ? SPEED_TOUCH : SPEED_MOUSE) * elapsed) / 1000;
        apply();
      }
      window.requestAnimationFrame(frame);
    }

    container.addEventListener('mouseenter', function () {
      hovered = true;
    });
    container.addEventListener('mouseleave', function () {
      hovered = false;
    });

    // Keyboard: stop, and bring the focused post fully into view
    container.addEventListener('focusin', function (event) {
      // Only for the keyboard. A tap or click also focuses the post, and
      // the row should carry on after those.
      try {
        if (!event.target.matches(':focus-visible')) return;
      } catch (error) {}
      hold();
      var item = event.target.closest ? event.target.closest('.instafeed-new-layout-item') : null;
      if (!item) return;
      var box = container.getBoundingClientRect();
      var rect = item.getBoundingClientRect();
      if (rect.left < box.left) pos -= box.left - rect.left;
      else if (rect.right > box.right) pos += rect.right - box.right;
      apply();
    });
    container.addEventListener('focusout', release);

    // Drag or swipe sideways
    container.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      drag = { x: event.clientX, id: event.pointerId };
      dragged = false;
      hold();
    });
    container.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.x;
      if (!dragged && Math.abs(dx) < DRAG_START) return;
      dragged = true;
      drag.x = event.clientX;
      pos -= dx;
      apply();
    });
    function endDrag(event) {
      if (!drag || (event && event.pointerId !== drag.id)) return;
      drag = null;
      release();
    }
    container.addEventListener('pointerup', endDrag);
    container.addEventListener('pointercancel', endDrag);
    container.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') endDrag(event);
    });
    // A drag with the mouse should not also open the post
    container.addEventListener(
      'click',
      function (event) {
        if (!dragged) return;
        dragged = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    apply();
    window.requestAnimationFrame(frame);
    return true;
  }

  function boot() {
    var feed = document.getElementById('insta-feed');
    if (!feed) return;

    var timer;
    var tries = 0;
    function attempt() {
      var container = feed.querySelector('.instafeed-new-layout-container');
      if (container && start(container)) {
        tries = 0;
        return;
      }
      // The app adds the posts first and sizes them a moment later
      if (container && ++tries < 40) later();
    }
    function later() {
      window.clearTimeout(timer);
      timer = window.setTimeout(attempt, 500);
    }

    // The app fills the row in when it comes near the screen, and rebuilds
    // it on some resizes
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        tries = 0;
        later();
      }).observe(feed, { childList: true, subtree: true });
    }
    window.addEventListener('resize', later);
    window.addEventListener('load', later);
    attempt();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
